// src/ui/overlays/InstallHint.tsx — Add-to-Home-Screen hint (PWA-05).
// Shows ~60s into first session if NOT already installed as PWA. Per-platform
// copy (iOS vs Android) per UI-SPEC §Copywriting Contract. Dismiss writes a
// localStorage flag so the user is never nagged again.

import { useEffect, useState, type JSX } from 'react';

import { detectPlatform, type PlatformFlags } from '../utils/platform';

import './overlays.css';

const DELAY_MS = 60_000; // PWA-05: ~60s into first session
const DISMISS_KEY = 'hunter:install-hint-dismissed';

// Exact UI-SPEC §Copywriting Contract strings
const COPY_IOS =
  "Add to Home Screen for the best experience. Tap Share then 'Add to Home Screen'.";
const COPY_ANDROID =
  "Install Hunter for the best experience. Tap the menu then 'Add to Home Screen'.";
const COPY_DISMISS = 'Got it';

export function InstallHint(): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [platform] = useState<PlatformFlags>(() => detectPlatform());

  useEffect(() => {
    // Already installed, or previously dismissed — never show.
    if (platform.isStandalone) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === 'true') return;
    } catch {
      // localStorage may throw in private mode — fail open (show the hint).
    }
    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return (): void => clearTimeout(t);
  }, [platform.isStandalone]);

  if (!visible) return null;

  let copy: string | null = null;
  if (platform.isIOS) copy = COPY_IOS;
  else if (platform.isAndroid) copy = COPY_ANDROID;
  else copy = null; // Desktop / other — don't show

  if (copy === null) return null;

  function dismiss(): void {
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // noop — same rationale as read
    }
    setVisible(false);
  }

  return (
    <div
      className="install-hint"
      data-testid="install-hint"
      role="dialog"
      aria-labelledby="install-hint-copy"
    >
      <p id="install-hint-copy" className="install-hint__copy">
        {copy}
      </p>
      <button
        type="button"
        className="install-hint__dismiss"
        data-testid="install-hint-dismiss"
        onClick={dismiss}
      >
        {COPY_DISMISS}
      </button>
    </div>
  );
}
